<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocale } from 'shared/composables/useLocale';
import { isSameDay, isSameYear, isValid } from 'date-fns';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  selectedStartDate: Date,
  selectedEndDate: Date,
  showMonthNavigation: {
    type: Boolean,
    default: false,
  },
  canNavigateNext: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['open', 'navigateMonth']);
const { t } = useI18n();
const { resolvedLocale } = useLocale();
const formatDate = (date, withYear = true) =>
  new Intl.DateTimeFormat(resolvedLocale.value, {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  }).format(date);

const isSingleDate = computed(
  () =>
    isValid(props.selectedStartDate) &&
    isValid(props.selectedEndDate) &&
    isSameDay(props.selectedStartDate, props.selectedEndDate)
);

const formatDateRange = computed(() => {
  const startDate = props.selectedStartDate;
  const endDate = props.selectedEndDate;

  if (!isValid(startDate) || !isValid(endDate)) {
    return t('DATE_PICKER.PLACEHOLDER');
  }

  if (isSingleDate.value) {
    return formatDate(startDate);
  }

  const crossesYears = !isSameYear(startDate, endDate);

  // Always show years when crossing year boundaries
  if (crossesYears) {
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }

  // For same year, always show the year for clarity
  return `${formatDate(startDate, false)} - ${formatDate(endDate)}`;
});

const openDatePicker = () => {
  emit('open');
};
</script>

<template>
  <div class="flex min-w-0 flex-wrap items-center gap-1 sm:inline-flex">
    <button
      class="inline-flex relative box-border min-h-11 min-w-0 max-w-full sm:min-h-8 items-center gap-2 rounded-lg border border-solid border-n-weak bg-transparent py-0 pl-2 pr-3.5 hover:bg-n-alpha-1 active:bg-n-alpha-1"
      @click="openDatePicker"
    >
      <Icon
        icon="i-lucide-calendar-range"
        class="text-n-slate-12 size-[18px] flex-shrink-0"
      />
      <span class="text-sm font-medium text-n-slate-12 truncate">
        {{ formatDateRange }}
      </span>
      <Icon
        icon="i-lucide-chevron-down"
        class="text-n-slate-12 size-4 flex-shrink-0"
        :class="{ invisible: isSingleDate }"
      />
    </button>
    <NextButton
      v-if="showMonthNavigation"
      v-tooltip.top="$t('DATE_PICKER.PREVIOUS_PERIOD')"
      slate
      faded
      sm
      icon="i-lucide-chevron-left"
      class="!size-11 sm:!size-8 rtl:rotate-180"
      @click="emit('navigateMonth', 'prev')"
    />
    <NextButton
      v-if="showMonthNavigation"
      v-tooltip.top="$t('DATE_PICKER.NEXT_PERIOD')"
      slate
      faded
      sm
      icon="i-lucide-chevron-right"
      class="!size-11 sm:!size-8 rtl:rotate-180"
      :disabled="!canNavigateNext"
      @click="emit('navigateMonth', 'next')"
    />
  </div>
</template>
